import { useUserStore } from "../store/user.js";

// Routes that don't require authentication
const publicRoutes = ["login", "auth-callback"];

// Permission helpers — mirror MainNav.vue's gating so direct URL visits are
// enforced the same way the sidebar/cards are. Real enforcement is still
// server-side (Directus rejects the data calls); this just avoids landing a
// user on a permission-less page.
function hasReadAccess(permissions, collection) {
  const access = permissions?.[collection]?.read;
  return access?.access !== undefined && access?.access !== 'none';
}

function hasWriteAccess(permissions, collection) {
  const access = permissions?.[collection];
  if (!access) return false;
  return access.create?.access !== 'none' || access.update?.access !== 'none';
}

function hasFullAccess(permissions, collection) {
  const access = permissions?.[collection];
  if (!access) return false;
  const fullRead = access.read?.access === 'full';
  const fullCreate = access.create?.access === 'full';
  const fullUpdate = access.update?.access === 'full';
  return fullRead && (fullCreate || fullUpdate);
}

// Returns true if the user may access the route given its meta requirements.
function canAccessRoute(meta, permissions) {
  if (!meta || !meta.collection) return true;
  if (!permissions) return false;
  if (meta.requiresFullAccess) return hasFullAccess(permissions, meta.collection);
  if (meta.requiresWrite) return hasWriteAccess(permissions, meta.collection);
  return hasReadAccess(permissions, meta.collection);
}

export const navigationGuard = async (to, from, next) => {

  const userStore = useUserStore();

  // Skip auth check for public routes
  if (publicRoutes.includes(to.name)) {
    if (to.name === "login" && userStore.isLoggedIn) {
      next({ name: "home" });
    } else {
      next();
    }
    return;
  }

  await userStore.checkAuth();

  if (!userStore.isLoggedIn) {
    // If user was previously logged in (had cached data), this is a session expiry
    const wasLoggedIn = localStorage.getItem('user') !== null;
    next({ name: "login", query: wasLoggedIn ? { expired: '1' } : undefined });
    return;
  }

  // Per-route permission enforcement for the admin area only. The rest of the
  // app relies on server-side Directus enforcement (unchanged); here we block
  // direct URL visits to admin pages the user has no permission for, matching
  // how MainNav hides the corresponding link/card. Check every matched record
  // (parent + child) so a route is blocked if any segment is off-limits.
  const isAdminRoute = to.matched.some((record) => record.path === "/admin");
  if (isAdminRoute) {
    const blocked = to.matched.some(
      (record) => !canAccessRoute(record.meta, userStore.permissions)
    );
    if (blocked) {
      next({ name: "home" });
      return;
    }
  }

  next();
};
